/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Table,
  Tag,
  Spin,
  Alert,
  Progress,
  Tooltip,
  Typography,
  Select,
  Button,
} from "antd";
import {
  DollarOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  CloseCircleOutlined,
  ArrowUpOutlined,
  EyeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { reportsApi, tripsApi } from "../../apis";
import { hasPermission } from "../../utils/permissions";
import moment from "moment";
import ResponsiveTable from "../../_components/ResponsiveTable";
const { Text } = Typography;
const { Option } = Select;

const COLORS = {
  primary: "#7B4D12",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#7B4D12",
  purple: "#8b5cf6",
  orange: "#f97316",
  cyan: "#8B6340",
};
const PIE_COLORS = [
  COLORS.primary,
  COLORS.success,
  COLORS.warning,
  COLORS.danger,
  COLORS.purple,
  COLORS.cyan,
];

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return moment(dateString).format("DD MMM YYYY");
  } catch {
    return dateString;
  }
};

// Helper function to format time (if needed)
const formatTime = (timeString) => {
  if (!timeString) return "N/A";
  try {
    return moment(timeString, "HH:mm:ss").format("hh:mm A");
  } catch {
    return timeString;
  }
};

const StatCard = ({
  title,
  value,
  prefix,
  icon,
  color,
  suffix = "",
  trend,
}) => (
  <Card
    bodyStyle={{ padding: "20px 24px" }}
    style={{
      borderRadius: 12,
      border: "1px solid #f0f0f5",
      boxShadow: "0 2px 8px rgba(0,0,0,.04)",
      height: "100%",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
      }}
    >
      <div style={{ flex: 1 }}>
        <Text
          type="secondary"
          style={{
            fontSize: 13,
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: ".04em",
          }}
        >
          {title}
        </Text>
        <div
          style={{
            marginTop: 8,
            fontSize: 28,
            fontWeight: 700,
            color: "#1a1d2e",
            lineHeight: 1.1,
          }}
        >
          {prefix}
          {typeof value === "number" ? value.toLocaleString() : value}
          {suffix}
        </div>
        {trend !== undefined && (
          <div
            style={{
              marginTop: 6,
              fontSize: 12,
              color: trend >= 0 ? COLORS.success : COLORS.danger,
              fontWeight: 500,
            }}
          >
            <ArrowUpOutlined
              style={{ transform: trend < 0 ? "rotate(180deg)" : "none" }}
            />{" "}
            {Math.abs(trend)}% vs last month
          </div>
        )}
      </div>
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: `${color}18`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { style: { fontSize: 22, color } })}
      </div>
    </div>
  </Card>
);

const Dashboard = () => {
  const history = useHistory();
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [utilization, setUtilization] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const userPermissions = currentUser?.permissions || [];

  // Check permissions
  const canViewDashboard = hasPermission(userPermissions, "reports.view");
  const canViewReports = hasPermission(userPermissions, "reports.view");

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear];

  const MONTH_NAMES = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);

      // Only fetch reports if user has view reports permission
      if (canViewReports) {
        const [sum, rev, monthly, util] = await Promise.all([
          reportsApi.getSummary(),
          reportsApi.getRevenuePerTrip(),
          reportsApi.getMonthlyRevenue(selectedYear),
          reportsApi.getSeatUtilization(),
        ]);
        setSummary(sum);
        setRevenueData(Array.isArray(rev) ? rev.slice(0, 8) : []);
        const monthlyArr = Array.isArray(monthly) ? monthly : [];
        setMonthlyRevenue(
          MONTH_NAMES.map((name, i) => {
            const found = monthlyArr.find((r) => Number(r.month) === i + 1);
            return {
              month: name,
              revenue: found ? Number(found.revenue) : 0,
              transactions: found ? Number(found.transactions) : 0,
            };
          }),
        );

        // Format the utilization data with proper dates
        const formattedUtilization = Array.isArray(util)
          ? util.map((item) => ({
              ...item,
              formattedDate: formatDate(item.date),
              formattedTime: item.time ? formatTime(item.time) : null,
              // If you need both date and time combined
              dateTime: item.time
                ? `${formatDate(item.date)} ${formatTime(item.time)}`
                : formatDate(item.date),
            }))
          : [];

        setUtilization(formattedUtilization.slice(0, 6));
      } else {
        // If user doesn't have reports view permission, still show limited dashboard info
        setSummary({
          totalRevenue: 0,
          totalReservations: 0,
          totalCustomers: 0,
          checkedIn: 0,
          totalTrips: 0,
          cancelledReservations: 0,
        });
        setRevenueData([]);
        setMonthlyRevenue([]);
        setUtilization([]);
      }
    } catch (e) {
      setError(
        "Failed to load dashboard data. Make sure the backend is running.",
      );
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canViewDashboard) {
      fetchAll();
    } else {
      setLoading(false);
    }
  }, [selectedYear, canViewDashboard, canViewReports]);

  const reservationStatusData =
    summary && canViewReports
      ? [
          {
            name: "Confirmed",
            value:
              summary.totalReservations -
              summary.cancelledReservations -
              summary.checkedIn,
          },
          { name: "Checked In", value: summary.checkedIn },
          { name: "Cancelled", value: summary.cancelledReservations },
        ].filter((d) => d.value > 0)
      : [];

  const utilizationColumns = [
    {
      title: "Date",
      dataIndex: "formattedDate",
      key: "date",
      render: (date) => <Text strong>{date}</Text>,
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
    {
      title: "Trip",
      dataIndex: "tripName",
      key: "tripName",
      render: (name) => <Tag color="volcano">{name}</Tag>,
    },
    {
      title: "Deck",
      dataIndex: "deckName",
      key: "deckName",
    },
    {
      title: "Utilization",
      key: "util",
      render: (_, r) => (
        <div style={{ minWidth: 120 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <Text style={{ fontSize: 12 }}>
              {r.reservedSeats}/{r.capacity}
            </Text>
            <Text
              strong
              style={{
                fontSize: 12,
                color:
                  r.utilizationPct >= 80
                    ? COLORS.success
                    : r.utilizationPct >= 50
                    ? COLORS.warning
                    : COLORS.danger,
              }}
            >
              {r.utilizationPct}%
            </Text>
          </div>
          <Progress
            percent={r.utilizationPct}
            size="small"
            showInfo={false}
            strokeColor={
              r.utilizationPct >= 80
                ? COLORS.success
                : r.utilizationPct >= 50
                ? COLORS.warning
                : COLORS.danger
            }
          />
        </div>
      ),
    },
    // Optional: Add a time column if needed
    ...(utilization.some((item) => item.time)
      ? [
          {
            title: "Time",
            dataIndex: "formattedTime",
            key: "time",
            render: (time) =>
              time ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {time}
                </Text>
              ) : null,
          },
        ]
      : []),
  ];

  // Permission check for viewing the dashboard
  if (!canViewDashboard) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              message="Access Denied"
              description="You don't have permission to access the dashboard. Please contact your administrator if you believe this is an error."
              type="error"
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Page header */}
          <div className="page-header">
            <div
              className="content-page-header"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div>
                <h5 style={{ margin: 0 }}>Dashboard Overview</h5>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Welcome back! Here is what is happening with your cruises.
                </Text>
              </div>
              {/* Optional: Add refresh button if needed */}
              {canViewReports && (
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchAll}
                  loading={loading}
                >
                  Refresh
                </Button>
              )}
            </div>
          </div>

          {error && (
            <Alert message={error} type="error" className="mb-4" showIcon />
          )}

          {loading && !summary ? (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              {/* Stat cards - Show limited stats if no report permission */}
              <Row gutter={[16, 16]} className="mb-4">
                {canViewReports ? (
                  <>
                    <Col xs={24} sm={12} lg={6}>
                      <StatCard
                        title="Total Revenue"
                        value={summary?.totalRevenue || 0}
                        prefix="EGP "
                        icon={<DollarOutlined />}
                        color={COLORS.success}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <StatCard
                        title="Total Reservations"
                        value={summary?.totalReservations || 0}
                        icon={<CheckCircleOutlined />}
                        color={COLORS.primary}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <StatCard
                        title="Customers"
                        value={summary?.totalCustomers || 0}
                        icon={<TeamOutlined />}
                        color={COLORS.purple}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <StatCard
                        title="Checked In"
                        value={summary?.checkedIn || 0}
                        icon={<BarChartOutlined />}
                        color={COLORS.orange}
                      />
                    </Col>
                  </>
                ) : (
                  <Col xs={24}>
                    <Alert
                      message="Limited Access"
                      description="You don't have permission to view detailed analytics. Please contact your administrator for full access."
                      type="info"
                      showIcon
                    />
                  </Col>
                )}
              </Row>

              {/* Charts row - Only show if user has report permission */}
              {canViewReports && (
                <>
                  <Row gutter={[16, 16]} className="mb-4">
                    {/* Monthly revenue */}
                    <Col xs={24} lg={16}>
                      <Card
                        title={
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              flexWrap: "wrap",
                              gap: 8,
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>
                              Monthly Revenue
                            </span>
                            <Select
                              value={selectedYear}
                              onChange={setSelectedYear}
                              style={{ width: 100 }}
                              size="small"
                            >
                              {years.map((y) => (
                                <Option key={y} value={y}>
                                  {y}
                                </Option>
                              ))}
                            </Select>
                          </div>
                        }
                        bodyStyle={{ padding: "16px 16px 8px" }}
                        style={{
                          borderRadius: 12,
                          border: "1px solid #f0f0f5",
                          height: "100%",
                        }}
                      >
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart
                            data={monthlyRevenue}
                            margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#f0f0f5"
                              vertical={false}
                            />
                            <XAxis
                              dataKey="month"
                              tick={{ fontSize: 12, fill: "#9ca3af" }}
                              axisLine={false}
                              tickLine={false}
                            />
                            <YAxis
                              tick={{ fontSize: 12, fill: "#9ca3af" }}
                              axisLine={false}
                              tickLine={false}
                              tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                            />
                            <RTooltip
                              formatter={(v) => [
                                `EGP ${Number(v).toLocaleString()}`,
                                "Revenue",
                              ]}
                              contentStyle={{
                                borderRadius: 8,
                                border: "1px solid #f0f0f5",
                                fontSize: 13,
                              }}
                            />
                            <Bar
                              dataKey="revenue"
                              fill={COLORS.primary}
                              radius={[6, 6, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Card>
                    </Col>

                    {/* Reservation status pie */}
                    <Col xs={24} lg={8}>
                      <Card
                        title={
                          <span style={{ fontWeight: 600 }}>
                            Reservation Status
                          </span>
                        }
                        bodyStyle={{ padding: "16px" }}
                        style={{
                          borderRadius: 12,
                          border: "1px solid #f0f0f5",
                          height: "100%",
                        }}
                      >
                        {reservationStatusData.length > 0 ? (
                          <>
                            <ResponsiveContainer width="100%" height={200}>
                              <PieChart>
                                <Pie
                                  data={reservationStatusData}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={55}
                                  outerRadius={85}
                                  paddingAngle={3}
                                  dataKey="value"
                                  label={({ name, percent }) =>
                                    `${(percent * 100).toFixed(0)}%`
                                  }
                                  labelLine={false}
                                >
                                  {reservationStatusData.map((_, i) => (
                                    <Cell
                                      key={i}
                                      fill={PIE_COLORS[i % PIE_COLORS.length]}
                                    />
                                  ))}
                                </Pie>
                                <RTooltip
                                  contentStyle={{
                                    borderRadius: 8,
                                    fontSize: 13,
                                  }}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                                marginTop: 8,
                              }}
                            >
                              {reservationStatusData.map((item, i) => (
                                <div
                                  key={item.name}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    fontSize: 13,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        background:
                                          PIE_COLORS[i % PIE_COLORS.length],
                                      }}
                                    />
                                    <Text>{item.name}</Text>
                                  </div>
                                  <Text strong>{item.value}</Text>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div
                            style={{
                              textAlign: "center",
                              padding: "40px 0",
                              color: "#9ca3af",
                            }}
                          >
                            No reservation data
                          </div>
                        )}
                      </Card>
                    </Col>
                  </Row>

                  {/* Revenue per trip */}
                  {revenueData.length > 0 && (
                    <Row gutter={[16, 16]} className="mb-4">
                      <Col xs={24}>
                        <Card
                          title={
                            <span style={{ fontWeight: 600 }}>
                              Revenue Per Trip
                            </span>
                          }
                          bodyStyle={{ padding: "16px 16px 8px" }}
                          style={{
                            borderRadius: 12,
                            border: "1px solid #f0f0f5",
                          }}
                        >
                          <ResponsiveContainer width="100%" height={220}>
                            <BarChart
                              data={revenueData}
                              margin={{
                                top: 5,
                                right: 10,
                                left: 0,
                                bottom: 40,
                              }}
                              layout="vertical"
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#f0f0f5"
                                horizontal={false}
                              />
                              <XAxis
                                type="number"
                                tick={{ fontSize: 12, fill: "#9ca3af" }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(v) =>
                                  `${(v / 1000).toFixed(0)}k`
                                }
                              />
                              <YAxis
                                dataKey="tripName"
                                type="category"
                                tick={{ fontSize: 11, fill: "#6b7280" }}
                                axisLine={false}
                                tickLine={false}
                                width={120}
                              />
                              <RTooltip
                                formatter={(v) => [
                                  `EGP ${Number(v).toLocaleString()}`,
                                  "Revenue",
                                ]}
                                contentStyle={{ borderRadius: 8, fontSize: 13 }}
                              />
                              <Bar
                                dataKey="totalRevenue"
                                fill={COLORS.info}
                                radius={[0, 6, 6, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </Card>
                      </Col>
                    </Row>
                  )}

                  {/* Seat utilization table */}
                  {utilization.length > 0 && (
                    <Row gutter={[16, 16]} className="mb-4">
                      <Col xs={24}>
                        <Card
                          title={
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <span style={{ fontWeight: 600 }}>
                                Recent Seat Utilization
                              </span>
                              <Button
                                size="small"
                                type="link"
                                onClick={() =>
                                  history.push("/reports/utilization")
                                }
                              >
                                View All
                              </Button>
                            </div>
                          }
                          bodyStyle={{ padding: 0 }}
                          style={{
                            borderRadius: 12,
                            border: "1px solid #f0f0f5",
                          }}
                        >
                          <ResponsiveTable
                            columns={utilizationColumns}
                            dataSource={utilization}
                            rowKey={(r, i) => `${r.date}-${r.deckName}-${i}`}
                            pagination={false}
                            size="small"
                            scroll={{ x: true }}
                          />
                        </Card>
                      </Col>
                    </Row>
                  )}
                </>
              )}

              {/* Quick action cards - Always show but with navigation restrictions */}
              <Row gutter={[16, 16]} className="mb-4">
                {[
                  {
                    title: "Manage Trips",
                    desc: "Create and schedule cruises",
                    path: "/trips",
                    color: COLORS.primary,
                    permission: "trips.view",
                  },
                  {
                    title: "Reservations",
                    desc: "View all bookings",
                    path: "/reservations",
                    color: COLORS.success,
                    permission: "reservations.view",
                  },
                  {
                    title: "Scan QR",
                    desc: "Board check-in",
                    path: "/tickets/scan",
                    color: COLORS.orange,
                    permission: "tickets.check_in",
                  },
                  {
                    title: "Full Reports",
                    desc: "Revenue analytics",
                    path: "/reports",
                    color: COLORS.purple,
                    permission: "reports.view",
                  },
                ].map((item) => {
                  const hasAccess = hasPermission(
                    userPermissions,
                    item.permission,
                  );

                  return (
                    <Col key={item.path} xs={24} sm={12} lg={6}>
                      <Card
                        hoverable={hasAccess}
                        onClick={() => hasAccess && history.push(item.path)}
                        bodyStyle={{ padding: "16px 20px" }}
                        style={{
                          borderRadius: 12,
                          border: `1.5px solid ${item.color}20`,
                          cursor: hasAccess ? "pointer" : "not-allowed",
                          transition: "border-color .2s, box-shadow .2s",
                          opacity: hasAccess ? 1 : 0.6,
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: item.color,
                            display: "block",
                          }}
                        >
                          {item.title}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          {item.desc}
                        </Text>
                        {!hasAccess && (
                          <Tag color="default" style={{ marginTop: 8 }}>
                            Access Restricted
                          </Tag>
                        )}
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
